import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher, MatSnackBar } from '@angular/material';
import { Router } from '@angular/router';
import { OtpEmailService } from './service/otp-email.service';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { MessageFromBackEnd } from './interfaces/response-message';

@Component({
  selector: 'app-otp-email',
  templateUrl: './otp-email.component.html',
  styleUrls: ['./otp-email.component.scss'],
})
export class OtpEmailComponent implements OnInit, OnDestroy {
  codeEmailForm: FormGroup;
  matcher = new MyErrorStateMatcher();
  messageBanner: string =
    `We've sent a code to your email on the previous page. Please enter it below for validation.`;
  labelButton: string = 'Continue';
  attemptCounter: number = 0;
  userDataSet = JSON.parse(localStorage.getItem('formFullContractor'));
  codeToValidate: number | string = 0;
  btnToShowSendCode: boolean = false;
  _subscriptionToDestroy$ = new Subject();
  loading$ = this._otpEmailService.loadingSendEmailSubject$;

  @HostListener('window:beforeunload', ['$event']) unloadNotification(
    $event: Event
  ): void {
    sessionStorage.setItem('isRefreshed', 'true');
  }

  constructor(
    private _router: Router,
    private _formBuilder: FormBuilder,
    private _otpEmailService: OtpEmailService,
    private _snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.initForm();
    this.sendCodeToEmail();
  }

  initForm(): void {
    this.codeEmailForm = this._formBuilder.group({
      code: [
        { value: '', disabled: true },
        [Validators.required, Validators.maxLength(9)],
      ],
    });
  }

  incrementAttempt(): void {
    this.attemptCounter++;
  }

  goBack(): void {
    localStorage.removeItem('formFullContractor');
    sessionStorage.removeItem('isRefreshed');
    this._router.navigate(['v2/full-contractor']);
  }

  goToAccountSelector(): void {
    if (this.codeEmailForm.valid) {
      if (this.codeEmailForm.get('code').value !== this.codeToValidate) {
        this.incrementAttempt();
        this.messageBanner = `The verification code is incorrect, please try again (${this.attemptCounter} of 4 times)`;
      }
      if (this.attemptCounter <= 4) {
        if (this.codeEmailForm.get('code').value === this.codeToValidate) {
          this.attemptCounter = 0;
          this._router.navigate(['v2/full-contractor/account-selector']);
        }
      } else {
        this.attemptCounter = 0;
        this.initForm();
        this.resendCodeToEmail();
      }
    }
  }

  resendCodeToEmail(): void {
    this.btnToShowSendCode = true;
    this.messageBanner =
      `We've sent a code to your email on the previous page. Please enter it below for validation.`;
    sessionStorage.removeItem('isRefreshed');
    this.sendCodeToEmail();
  }

  sendCodeToEmail(): void {
    if (this.userDataSet != null) {
      if (!sessionStorage.getItem('isRefreshed')) {
        this._otpEmailService
          .sendCodeViaEmail(this.userDataSet.email)
          .pipe(
            take(1),
            takeUntil(this._subscriptionToDestroy$),
            catchError((error) => {
              this._snackBar.open(
                'Oops, an error occurred when trying to send a code via email.',
                'OK',
                {
                  verticalPosition: 'top',
                  duration: 1500,
                }
              ).afterOpened().pipe(takeUntil(this._subscriptionToDestroy$)).subscribe(() => this.goBack());
              throw `Server side: ${JSON.stringify({error})}`;
            })
          )
          .subscribe((value: MessageFromBackEnd): void => {
            if (value.status !== 404) {
              this._snackBar.open(
                this.btnToShowSendCode
                  ? 'A new code has been successfully sent to your email.'
                  : 'A code has been successfully sent to your email.',
                'OK',
                {
                  verticalPosition: 'top',
                  duration: 1500,
                }
              );
              this.btnToShowSendCode = false;
              this.codeToValidate = value.data;
              sessionStorage.setItem('codeEmail', this.codeToValidate);
              this._otpEmailService.loadingSendEmailSubject.next(false);
              this.codeEmailForm.get('code').enable();
            } else {
              this._snackBar.open(value.message, 'OK', {
                verticalPosition: 'top',
                duration: 1500,
              });
              this._otpEmailService.loadingSendEmailSubject.next(false);
            }
          });
      } else {
        this.codeToValidate = sessionStorage.getItem('codeEmail');
        this._otpEmailService.loadingSendEmailSubject.next(false);
        this.codeEmailForm.get('code').enable();
      }
    } else {
      this.goBack();
      this._otpEmailService.loadingSendEmailSubject.next(false);
    }
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    sessionStorage.removeItem('isRefreshed');
    sessionStorage.removeItem('codeEmail');
    this._subscriptionToDestroy$.next(true);
    this._subscriptionToDestroy$.complete();
  }
}

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}
